// components/ImportOrdersModal.jsx - Modern redesign with full import logic
import { useState } from 'react';
import axios from 'axios';
import API_BASE_PATH from '../apiConfig';
import { showToast } from './Toast';
import { formatDateTimeLocal, calculateFees } from '../utils';

const ImportOrdersModal = ({ onClose, onImportComplete }) => {
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState('');
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState(null);

  // Format strike price helper
  const formatStrikePrice = (strike) => {
    const strikeValue = parseFloat(strike) / 1000;
    return strikeValue % 1 === 0 ? strikeValue.toFixed(0) : strikeValue.toFixed(2);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelection(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelection = (selectedFile) => {
    if (selectedFile && selectedFile.name.endsWith('.csv')) {
      setFile(selectedFile);
      
      // Read the file
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        setCsvData(text);
        showToast('File loaded successfully', 'success', 2000);
      };
      reader.readAsText(selectedFile);
    } else {
      showToast('Please select a valid CSV file', 'error');
      setFile(null);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelection(e.target.files[0]);
    }
  };

  const handleImport = async () => {
    if (!file || !csvData) {
      showToast('Please select a file first', 'warning');
      return;
    }

    setLoading(true);
    setImportProgress(0);
    setImportStatus(null);

    try {
      // Parse CSV data
      const rows = csvData.split("\n").slice(1); // Skip header row
      const newOrders = rows
        .map((row) => {
          if (!row.trim()) return null;
          const columns = row.split(",");
          const [symbol, , side, , filled, , , avgPrice, , , filledTime] = columns;
          
          // Skip if filled equals 0
          if (parseInt(filled, 10) === 0) return null;
          
          const baseSymbolLength = symbol.match(/[A-Z]+/)[0].length;
          const baseSymbol = symbol.slice(0, baseSymbolLength);
          const date = symbol.slice(baseSymbolLength, baseSymbolLength + 6);
          const type = symbol.slice(baseSymbolLength + 6, baseSymbolLength + 7);
          const strike = symbol.slice(baseSymbolLength + 7);
          
          const year = `20${date.slice(0, 2)}`;
          const month = date.slice(2, 4);
          const day = date.slice(4, 6);
          const formattedDate = `${day}${new Date(year, month - 1).toLocaleString("default", { month: "short" }).toUpperCase()}${year.slice(-2)}`;
          const formattedStrike = formatStrikePrice(strike);
          const formattedSymbol = `${baseSymbol} ${formattedStrike}${type} ${formattedDate}`;
          
          const conPrice = avgPrice ? Math.round(parseFloat(avgPrice) * 100) / 100 : 0;
          
          return {
            option_name: formattedSymbol,
            buy_sell: side,
            quantity: parseInt(filled, 10) || 0,
            filled: formatDateTimeLocal(filledTime) || "",
            price: conPrice,
            fees: calculateFees({
              buy_sell: side,
              quantity: parseInt(filled, 10) || 0,
              price: conPrice
            })
          };
        })
        .filter(order => order !== null);

      // Check for duplicates and save unique orders
      const uniqueNewOrders = [];
      const failedOrders = [];
      
      for (let i = 0; i < newOrders.length; i++) {
        const newOrder = newOrders[i];
        try {
          const response = await axios.get(`${API_BASE_PATH}/check-order`, {
            params: {
              option_name: newOrder.option_name,
              buy_sell: newOrder.buy_sell,
              quantity: newOrder.quantity,
              filled: newOrder.filled,
              price: newOrder.price,
            },
          });
          
          if (!response.data.exists) {
            uniqueNewOrders.push(newOrder);
          }
        } catch (error) {
          failedOrders.push({ order: newOrder, error: error.message });
        }
        
        // Update progress
        setImportProgress(Math.round(((i + 1) / newOrders.length) * 100));
      }

      // Save unique orders
      if (uniqueNewOrders.length > 0) {
        try {
          await axios.post(`${API_BASE_PATH}/trades`, uniqueNewOrders);
          
          const status = {
            success: true,
            processed: newOrders.length,
            saved: uniqueNewOrders.length,
            failed: failedOrders.length,
            errors: failedOrders,
          };
          
          setImportStatus(status);
          showToast(
            `Successfully imported ${uniqueNewOrders.length} of ${newOrders.length} orders`,
            'success'
          );
          
          setTimeout(() => {
            onImportComplete();
          }, 2000);
        } catch (error) {
          const status = {
            success: false,
            processed: newOrders.length,
            saved: 0,
            failed: newOrders.length,
            errors: [{ error: error.message }],
          };
          
          setImportStatus(status);
          showToast('Failed to save orders to database', 'error');
        }
      } else {
        const status = {
          success: true,
          processed: newOrders.length,
          saved: 0,
          failed: failedOrders.length,
          errors: failedOrders,
        };
        
        setImportStatus(status);
        showToast('No new orders to import (all already exist)', 'warning');
      }
    } catch (error) {
      showToast('Failed to parse CSV file', 'error');
      console.error('Import error:', error);
    } finally {
      setLoading(false);
      setImportProgress(0);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container modern" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2 className="modal-title">
            <i className="fas fa-file-import"></i>
            Import Orders
          </h2>
          <button className="modal-close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {/* Progress Bar */}
          {loading && importProgress > 0 && (
            <div className="import-progress-container">
              <div className="import-progress-bar">
                <div 
                  className="import-progress-fill" 
                  style={{ width: `${importProgress}%` }}
                >
                  <span className="import-progress-text">{importProgress}%</span>
                </div>
              </div>
              <p className="import-progress-label">
                Processing orders... Please wait
              </p>
            </div>
          )}

          {/* Import Status Summary */}
          {importStatus && (
            <div className={`import-status ${importStatus.success ? 'success' : 'error'}`}>
              <div className="import-status-header">
                <i className={`fas ${importStatus.success ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
                <h3>Import {importStatus.success ? 'Complete' : 'Failed'}</h3>
              </div>
              <div className="import-status-stats">
                <div className="import-stat">
                  <span className="import-stat-label">Processed:</span>
                  <span className="import-stat-value">{importStatus.processed}</span>
                </div>
                <div className="import-stat">
                  <span className="import-stat-label">Saved:</span>
                  <span className="import-stat-value success">{importStatus.saved}</span>
                </div>
                {importStatus.failed > 0 && (
                  <div className="import-stat">
                    <span className="import-stat-label">Failed:</span>
                    <span className="import-stat-value error">{importStatus.failed}</span>
                  </div>
                )}
              </div>
              {importStatus.errors && importStatus.errors.length > 0 && (
                <details className="import-errors">
                  <summary>View Errors ({importStatus.errors.length})</summary>
                  <ul>
                    {importStatus.errors.map((err, idx) => (
                      <li key={idx}>
                        {err.order?.option_name || 'Unknown'}: {err.error}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          {/* File Upload Area - Hide during processing */}
          {!loading && (
            <div 
              className={`file-upload-area ${dragActive ? 'drag-active' : ''} ${file ? 'has-file' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {!file ? (
                <>
                  <i className="fas fa-cloud-upload-alt file-upload-icon"></i>
                  <p className="file-upload-text">
                    Drag and drop your CSV file here
                  </p>
                  <p className="file-upload-subtext">or</p>
                  <label className="btn-file-select">
                    <i className="fas fa-folder-open"></i>
                    Choose File
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                  </label>
                </>
              ) : (
                <div className="file-selected">
                  <i className="fas fa-file-csv file-selected-icon"></i>
                  <div className="file-selected-info">
                    <p className="file-selected-name">{file.name}</p>
                    <p className="file-selected-size">
                      {(file.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                  <button 
                    className="btn-file-remove"
                    onClick={() => {
                      setFile(null);
                      setCsvData('');
                      setImportStatus(null);
                      showToast('File removed', 'warning', 2000);
                    }}
                  >
                    <i className="fas fa-trash-alt"></i>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Info Box */}
          {!loading && !importStatus && (
            <div className="info-box">
              <i className="fas fa-info-circle"></i>
              <div>
                <strong>CSV Format Requirements:</strong>
                <ul>
                  <li>Standard Webull options export format</li>
                  <li>Columns: Symbol, Side, Filled, Avg Price, Filled Time, etc.</li>
                  <li>Orders with 0 quantity will be skipped</li>
                  <li>Duplicate orders will be automatically detected</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button 
            className="btn btn-secondary" 
            onClick={onClose}
            disabled={loading}
          >
            {importStatus ? 'Close' : 'Cancel'}
          </button>
          {!importStatus && (
            <button 
              className="btn btn-primary" 
              onClick={handleImport}
              disabled={!file || loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Processing...
                </>
              ) : (
                <>
                  <i className="fas fa-upload"></i>
                  Import Orders
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportOrdersModal;